<template>
  <div class="ka-trend">
    <div class="ka-trend__header">
      <div>
        <h3 class="ka-trend__title">Daily Kiosk Activity</h3>
        <p class="ka-trend__subtitle">Sessions, bookings and video calls per day</p>
      </div>
      <div class="ka-trend__legend">
        <span v-for="item in legend" :key="item.name" class="ka-trend__legend-item">
          <span
            class="ka-trend__legend-dot"
            :class="{ 'ka-trend__legend-dot--line': item.line }"
            :style="{ background: item.color }"
          />
          {{ item.name }}
        </span>
      </div>
    </div>

    <ja-apex-chart
      v-if="daily.length"
      type="line"
      :height="320"
      :options="chartOptions"
      :series="series"
    />
    <div v-else class="ka-trend__empty">No kiosk activity in this period</div>
  </div>
</template>

<script>
import JaApexChart from "../../journey-analytics/components/JaApexChart.vue";
import { COLORS } from "../kioskAnalyticsHelpers";

const LEGEND = [
  { name: "Sessions", color: COLORS.sessions, line: true },
  { name: "Self-service bookings", color: COLORS.selfService },
  { name: "Video calls", color: COLORS.videoCalls },
  { name: "Completed", color: COLORS.completed, line: true },
];

export default {
  name: "KioskDailyTrend",
  components: { JaApexChart },
  props: {
    daily: { type: Array, required: true },
  },
  data() {
    return { legend: LEGEND };
  },
  computed: {
    categories() {
      return this.daily.map((d) => {
        const date = new Date(`${d.date}T00:00:00`);
        return Number.isNaN(date.getTime())
          ? d.date
          : date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
      });
    },
    series() {
      return [
        { name: "Sessions", type: "line", data: this.daily.map((d) => d.sessions) },
        { name: "Self-service bookings", type: "column", data: this.daily.map((d) => d.selfServiceBookings) },
        { name: "Video calls", type: "column", data: this.daily.map((d) => d.videoCalls) },
        { name: "Completed", type: "line", data: this.daily.map((d) => d.completed) },
      ];
    },
    chartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit", zoom: { enabled: false } },
        colors: LEGEND.map((l) => l.color),
        stroke: { width: [3, 0, 0, 2], curve: "smooth", dashArray: [0, 0, 0, 5] },
        plotOptions: { bar: { columnWidth: "52%", borderRadius: 4 } },
        markers: { size: [4, 0, 0, 0], strokeWidth: 0 },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.categories,
          labels: { style: { colors: "#6e6b7b" }, rotate: -45, hideOverlappingLabels: true },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tickAmount: Math.min(this.categories.length, 14),
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { colors: "#6e6b7b" },
            formatter: (val) => Math.round(val).toLocaleString("en-GB"),
          },
        },
        legend: { show: false },
        grid: { borderColor: "#f3f2f7", strokeDashArray: 4, padding: { right: 8 } },
        tooltip: {
          shared: true,
          intersect: false,
          y: { formatter: (val) => Number(val || 0).toLocaleString("en-GB") },
        },
      };
    },
  },
};
</script>

<style scoped lang="scss">
.ka-trend {
  background: #fff;
  border-radius: 18px;
  padding: 1.35rem 1.5rem 1rem;
  border: 1px solid #ececf2;
  box-shadow: 0 1px 2px 0 rgba(20, 21, 34, 0.04);
  height: 100%;
  box-sizing: border-box;
  min-width: 0;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.5rem;
  }

  &__title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #2d2d2d;
    margin: 0 0 0.3rem;
  }

  &__subtitle {
    font-size: 0.8125rem;
    color: #a8a8b3;
    margin: 0;
  }

  &__legend {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.6rem 1rem;
    padding-top: 0.15rem;
  }

  &__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #6b7280;
    white-space: nowrap;
  }

  &__legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;

    &--line {
      height: 3px;
      width: 14px;
      border-radius: 2px;
    }
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 320px;
    color: #a8a8b3;
    font-size: 0.875rem;
  }
}

@media (max-width: 768px) {
  .ka-trend__header {
    flex-direction: column;
  }

  .ka-trend__legend {
    justify-content: flex-start;
  }
}
</style>
